<template>
  <UForm
    ref="form"
    :schema
    :data-disabled="disabled"
    :state="stateRef"
    v-bind="$attrs"
    class="relative group"
    @submit="submitHandler"
  >
    <UAlert
      v-if="lastError"
      :title="lastError.message"
      :color="lastError.color as any"
      variant="subtle"
      class="relative mb-3"
    >
      <template v-if="lastError.data" #description>
        <ul v-if="Array.isArray(lastError.data)">
          <li v-for="value in lastError.data" :key="value">{{ value }}</li>
        </ul>
        <p v-else-if="typeof lastError.data === 'string'">
          {{ lastError.data }}
        </p>
        <ul v-else-if="typeof lastError.data === 'object'">
          <li v-for="[key, value] in Object.entries(lastError.data)" :key>
            {{ value }}
          </li>
        </ul>
      </template>
    </UAlert>

    <slot :state="stateRef" :submit="form?.submit" :setState />

    <div
      v-if="showSubmit"
      class="flex gap-4 items-center mt-5"
      v-bind="submitWrapperAttrs"
    >
      <slot name="before-submit" />

      <slot name="submitButton">
        <UButton
          :loading="isButtonLoading"
          type="submit"
          v-bind="{
            ...submitAttrs,
            color: disabled ? 'gray' : submitAttrs.color,
          }"
          :disabled
        >
          <template v-if="submitLabel">{{ submitLabel }}</template>
          <template v-else>
            {{
              stateRef.id
                ? $t("components.forms.AForm.edit")
                : $t("components.forms.AForm.submit")
            }}
          </template>
          <UTooltip
            v-if="contentLocale && localized"
            :content="
              $t('components.forms.AForm.localized', { locale: contentLocale })
            "
          >
            <AImg
              :src="$countryFlag(contentLocale)"
              class="rounded"
              width="24"
            />
          </UTooltip>
        </UButton>
      </slot>

      <slot name="after-submit" />
    </div>

    <div v-if="disabled" class="inset-0 absolute"></div>
  </UForm>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import type { AFormProps, LastError, ProvideModal } from '#common/types'
import type { Form, FormSubmitEvent } from '#ui/types'
import type { z } from 'zod'

const props = withDefaults(defineProps<AFormProps<T>>(), {
  closeModalAfterSubmit: true,
  disabled: false,
  loader: true,
  localized: false,
  omitAttrs: () => [],
  preSubmit: async () => true,
  showSubmit: true,
  submitAttrs: () => ({}),
  submitWrapperAttrs: () => ({}),
})

const emit = defineEmits<Emits>()
const { contentLocale } = storeToRefs(useLocaleStore())
const isSubmitting = ref(false)
const forceShowLoader = ref(false)
const isButtonLoading = computed(
  () => props.loader && (isSubmitting.value || forceShowLoader.value),
)

const lastError = ref<LastError>(undefined)

const { state: stateRef } = toRefs<any>(props)

const modal = inject<ProvideModal>('modal', null)

function setState(newState: T) {
  stateRef.value = newState
}

const form = ref<Form<T>>()

provide('form', form)
provide<any>('state', stateRef)

type Emits = {
  created: [data: SubmitEvent]
  errored: [data: SubmitEvent, res: any, lastError: Ref<LastError>]
  erroring: [data: SubmitEvent, res: any]
  handled: [data: SubmitEvent, res: any]
  handling: [data: SubmitEvent]
  success: [data: SubmitEvent, res: any]
  updated: [data: SubmitEvent, res: any]
}
type Schema = z.output<Required<AFormProps<T>>['schema']>

type SubmitEvent = FormSubmitEvent<Schema>

const currentErrors = ref<any>(null)
function setErrors(errors: any) {
  form.value!.setErrors(
    Object.entries<string[]>(errors).map(([key, value]) => ({
      message: value.join(' '),
      path: key,
    })),
  )
}

async function submitHandler(submitEvent: FormSubmitEvent<Schema>) {
  if (currentErrors.value) {
    form.value!.setErrors(currentErrors.value)
  }

  if (typeof props.handler === 'undefined')
    return

  const shouldSubmit = await props.preSubmit()

  if (!shouldSubmit) {
    return
  }

  emit('handling', submitEvent)

  isSubmitting.value = true
  forceShowLoader.value = props.loader

  const loaderPromise = new Promise(resolve =>
    setTimeout(resolve, props.loader ? (process.env.TEST ? 20 : 400) : 0),
  ).then(() => {
    forceShowLoader.value = false
  })

  const filteredSubmitEvent = {
    ...submitEvent,
    data: Object.fromEntries(
      Object.entries(submitEvent.data).filter(
        ([key]) => !props.omitAttrs.includes(key),
      ),
    ) as T,
  }

  const promise = props.handler!(filteredSubmitEvent)

  const event = submitEvent.data.id ? 'updated' : 'created'

  Promise.all([promise, loaderPromise])
    .then(([res]) => {
      currentErrors.value = null
      form.value!.clear()

      emit(event as any, submitEvent)
      emit('handled', submitEvent, res)
      emit('success', submitEvent, res)

      if (props.closeModalAfterSubmit && modal) {
        modal.close()
      }
    })
    .catch((err: any) => {
      emit('erroring', submitEvent, err)

      if (err.status === 422) {
        const errors = Object.entries<string[]>(err.data.data).map(
          ([key, value]) => ({ message: value.join(' '), path: key }),
        )
        form.value!.setErrors(errors)
        currentErrors.value = errors
        return
      }

      if (
        !('statusText' in err)
        || !('status' in err)
        || !('response' in err)
        || !('data' in err)
      ) {
        return
      }

      lastError.value = {
        color: err.data.color ?? 'red',
        data: err.data.data,
        message: err.data.message,
        status: err.status,
        statusText: err.statusText,
      }

      emit('errored', submitEvent, err, lastError as any)
    })
    .finally(() => {
      isSubmitting.value = false
    })
}

defineExpose({
  setErrors,
  submit: () => form.value?.submit(),
})
</script>
